'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import Image from 'next/image'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowUpRightIcon,
  CalendarIcon,
  EditIcon,
  EyeIcon,
  FileIcon,
  ImageIcon,
  MoreHorizontalIcon,
  BookOpenIcon,
  SquareMenuIcon,
  SquarePlusIcon,
  Trash2Icon,
  RefreshCwIcon
} from 'lucide-react'
import Flag from 'react-flagkit'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DeleteConfirmationDialog,
  LoadingState,
  SortableHeader,
  TableSortActions,
  CataloguesFilters
} from '@/components/partials'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import {
  formatDateTime,
  getCountryCodeFromLocale,
  getLocalizedValue,
  getResizedPictureUrl,
  textLimit
} from '@/lib/utils'
import { useCatalogues, useDeleteCatalogue, useBulkDeleteCatalogues, useSortCatalogue } from '@/hooks'
import { SortEnum, SortOrderEnum } from '@/enums'
import { CataloguesFilterParams } from '@/types/catalogue'
import { useCataloguesStore, useMetaStore, useMeStore } from '@/store'
import { hasPermission } from '@/lib/permissions'
import { BulkDeleteResponse } from '@/types/general'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteCatalogueId, setDeleteCatalogueId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)

  // Catalogue selection hooks from Zustand store
  const { mutate: sortCatalogue, isPending: isSorting } = useSortCatalogue()
  const selectedCatalogueIds = useCataloguesStore(state => state.selectedCatalogueIds)
  const toggleCatalogueSelection = useCataloguesStore(state => state.toggleCatalogueSelection)
  const selectAllCatalogues = useCataloguesStore(state => state.selectAllCatalogues)
  const deselectAllCatalogues = useCataloguesStore(state => state.deselectAllCatalogues)
  const isCatalogueSelected = useCataloguesStore(state => state.isCatalogueSelected)

  const { metaState } = useMetaStore()
  const { meState } = useMeStore()
  const currentUser = meState.response
  const canCreate = hasPermission(currentUser, 'CATALOGUES', 'CREATE')
  const canShow = hasPermission(currentUser, 'CATALOGUES', 'SHOW')
  const canUpdate = hasPermission(currentUser, 'CATALOGUES', 'UPDATE')
  const canDelete = hasPermission(currentUser, 'CATALOGUES', 'DELETE')
  const defaultLocale = metaState.response?.generalSettings.adminDefaultLocale
  const { mutate: deleteCatalogueMutate, isPending: isDeleting } = useDeleteCatalogue()
  const { mutate: bulkDeleteCataloguesMutate, isPending: isBulkDeleting } = useBulkDeleteCatalogues()

  const filters = useMemo<CataloguesFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.ASC,
      sortBy: params.get('sortBy') || 'order',
      q: params.get('q') || '',
      isBlocked: params.get('isBlocked') === 'true' ? true : undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])
  const { data: cataloguesData, isLoading, error, refetch, isFetching } = useCatalogues(filters)

  // Update URL when filters change
  const updateUrlParams = (newFilters: Partial<CataloguesFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
          params.set(key, value.join(','))
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<CataloguesFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/galleries/catalogues')
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy, sort, page: 1 })
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleView = (id: string) => {
    router.push(`/galleries/catalogues/${id}`)
  }

  const handleEdit = (id: string) => {
    router.push(`/galleries/catalogues/${id}/edit`)
  }

  const handleDelete = (id: string) => {
    setDeleteCatalogueId(id)
  }

  const handleSortOrder = (id: string, type: SortOrderEnum) => {
    sortCatalogue(
      { id, type },
      {
        onSuccess: async updatedCatalogue => {
          queryClient.setQueryData(['catalogue', id], updatedCatalogue)
          await queryClient.invalidateQueries({ queryKey: ['catalogues'] })
          toast.success(t('sorted_successfully'))
        },
        onError: error => {
          console.error('Error sorting catalogue:', error)
          toast.error(t('failed_to_sort'))
        }
      }
    )
  }

  const renderActionItems = (id: string, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        {canShow && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleView(id)
            }}>
            <EyeIcon className="h-4 w-4" />
            {t('catalogue_details')}
          </ItemComponent>
        )}

        {canUpdate && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleEdit(id)
            }}>
            <EditIcon className="h-4 w-4" />
            {t('edit_catalogue')}
          </ItemComponent>
        )}
        {canDelete && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleDelete(id)
            }}
            className="text-destructive focus:text-destructive">
            <Trash2Icon className="h-4 w-4" />
            {t('delete')}
          </ItemComponent>
        )}
      </>
    )
  }

  const confirmDeleteCatalogue = () => {
    if (deleteCatalogueId) {
      deleteCatalogueMutate(deleteCatalogueId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['catalogues'] })
          toast.success(t('deleted_successfully'))
          setDeleteCatalogueId(null)
        },
        onError: error => {
          console.error('Error deleting catalogue:', error)
          toast.error(t('error_deleting'))
          setDeleteCatalogueId(null)
        }
      })
    }
  }

  const handleBulkDelete = () => {
    if (selectedCatalogueIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteCatalogues = () => {
    if (selectedCatalogueIds.length > 0) {
      bulkDeleteCataloguesMutate(selectedCatalogueIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['catalogues'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response) {
            if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }

          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const handleSelectAll = () => {
    if (cataloguesData && cataloguesData.items.length > 0) {
      if (selectedCatalogueIds.length === cataloguesData.items.length) {
        // If all catalogues are selected, deselect all
        deselectAllCatalogues()
      } else {
        // Otherwise, select all catalogues
        selectAllCatalogues(cataloguesData.items)
      }
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BookOpenIcon className="h-5 w-5" />
                {t('catalogues')}
              </CardTitle>
              <CardDescription>{t('catalogues_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {canCreate && (
                <Button asChild>
                  <Link href="/galleries/catalogues/create">
                    <SquarePlusIcon />
                    <span className="hidden items-center gap-2 lg:flex">{t('create_catalogue')}</span>
                  </Link>
                </Button>
              )}
              {canDelete && selectedCatalogueIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedCatalogueIds.length})</span>
                  </div>
                </Button>
              )}
              <CataloguesFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {cataloguesData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            {canDelete && (
                              <Checkbox
                                checked={
                                  cataloguesData &&
                                  cataloguesData.items.length > 0 &&
                                  selectedCatalogueIds.length === cataloguesData.items.length
                                }
                                onCheckedChange={() => {
                                  handleSelectAll()
                                }}
                                onClick={e => {
                                  e.stopPropagation()
                                }}
                                className="cursor-pointer"
                              />
                            )}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-37.5 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <ImageIcon className="h-4 w-4" />
                            {t('picture')}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy={`translations.${locale}.title`}
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center gap-2">
                              <Flag country={getCountryCodeFromLocale(locale)} size={16} />
                              {t('title')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-30 text-center">
                          <SortableHeader
                            sortBy="order"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <ArrowUpRightIcon className="h-4 w-4" />
                            {t('sort_order')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cataloguesData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        cataloguesData.items.map((catalogue, index) => (
                          <ContextMenu key={catalogue.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => canShow && handleView(catalogue.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    {canDelete && (
                                      <Checkbox
                                        checked={isCatalogueSelected(catalogue.id)}
                                        onCheckedChange={() => toggleCatalogueSelection(catalogue.id)}
                                        onClick={e => {
                                          e.stopPropagation()
                                        }}
                                        className="cursor-pointer"
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="flex min-h-20 items-center justify-center py-4">
                                  {catalogue.picture ? (
                                    <Image
                                      src={getResizedPictureUrl(catalogue.picture.path, 90, 90)}
                                      alt={catalogue.picture.name || ''}
                                      width={90}
                                      height={90}
                                      className="mx-auto max-h-22.5 w-auto border object-contain"
                                    />
                                  ) : (
                                    <div className="flex justify-center">
                                      <FileIcon className="text-muted-foreground h-10 w-10" />
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span>
                                      {textLimit(getLocalizedValue(catalogue, 'title', locale, defaultLocale), 120)}
                                    </span>
                                    <span className="text-muted-foreground text-xs">ID: {catalogue.id}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="flex flex-row items-center justify-center gap-2">
                                    <span className="text-sm">{catalogue.order}</span>
                                    {canUpdate && (
                                      <TableSortActions
                                        onSortUp={() => handleSortOrder(catalogue.id, SortOrderEnum.UP)}
                                        onSortDown={() => handleSortOrder(catalogue.id, SortOrderEnum.DOWN)}
                                        disabled={isSorting}
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(catalogue.createdAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(catalogue.updatedAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={e => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(catalogue.id, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(catalogue.id, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={cataloguesData.page}
                  totalPages={cataloguesData.pages}
                  totalItems={cataloguesData.total}
                  pageSize={cataloguesData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      {canDelete && (
        <DeleteConfirmationDialog
          open={!!deleteCatalogueId}
          onCancel={() => setDeleteCatalogueId(null)}
          onConfirm={confirmDeleteCatalogue}
          isDeleting={isDeleting}
        />
      )}

      {canDelete && (
        <DeleteConfirmationDialog
          open={bulkDeleteConfirmOpen}
          onCancel={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={confirmBulkDeleteCatalogues}
          isDeleting={isBulkDeleting}
          description={t('bulk_delete_confirmation_message')}
        />
      )}
    </>
  )
}

export default List
